/**
 * Support ticket list — `/app/support`. Shows the current org's tickets from
 * GET /support/tickets (scoped server-side to the signed org). The "New ticket"
 * action is gated on `canSubmit` from GET /support/access; view-only admins see
 * the list + detail but no submit form. Handles loading / empty / error and the
 * 503 "support unconfigured" state gracefully.
 */
import { Badge, Button, Card, Spinner } from '@fc/ui'
import { useQuery } from '@tanstack/react-query'
import { LifeBuoy, Plus } from 'lucide-react'
import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { listTickets, type TicketSummary } from '../../lib/support-api'
import {
  humanize,
  isUnconfigured,
  priorityVariant,
  statusVariant,
  SupportHeader,
  SupportUnconfigured,
  useSupportAccess,
} from './shared'

const selectClass =
  'h-9 rounded-md border border-border bg-bg-alt px-3 text-sm text-fg ' +
  'focus-visible:outline-none focus-visible:border-secondary focus-visible:ring-[3px] focus-visible:ring-ring'

const STATUS_FILTERS = ['', 'open', 'pending', 'resolved', 'closed'] as const

export function SupportList() {
  const navigate = useNavigate()
  const access = useSupportAccess()
  const [status, setStatus] = useState('')

  const tickets = useQuery({
    queryKey: ['support-tickets', status],
    queryFn: () => listTickets(status ? { status } : {}),
    retry: false,
  })

  // 503 from either query => support isn't configured on this server.
  if (isUnconfigured(access.error) || isUnconfigured(tickets.error)) {
    return (
      <div className="space-y-6">
        <SupportHeader />
        <SupportUnconfigured />
      </div>
    )
  }

  const canSubmit = access.data?.canSubmit ?? false
  const list = tickets.data?.tickets ?? []

  return (
    <div className="space-y-6">
      <SupportHeader
        actions={
          canSubmit ? (
            <Button onClick={() => navigate('/app/support/new')}>
              <Plus />
              New ticket
            </Button>
          ) : undefined
        }
      />

      <div className="flex items-center gap-2">
        <label htmlFor="support-status" className="text-sm text-muted">
          Status
        </label>
        <select
          id="support-status"
          value={status}
          onChange={(e) => setStatus(e.target.value)}
          className={selectClass}
        >
          {STATUS_FILTERS.map((s) => (
            <option key={s || 'all'} value={s}>
              {s ? humanize(s) : 'All'}
            </option>
          ))}
        </select>
      </div>

      <Card className="overflow-hidden p-0">
        {tickets.isLoading ? (
          <div className="flex items-center justify-center gap-2 py-20 text-muted">
            <Spinner /> Loading tickets…
          </div>
        ) : tickets.isError ? (
          <div className="space-y-2 py-20 text-center">
            <p className="text-sm text-danger">Couldn’t load support tickets.</p>
            <p className="text-xs text-dim">
              {tickets.error instanceof Error
                ? tickets.error.message
                : 'The support service may be unavailable.'}
            </p>
          </div>
        ) : list.length === 0 ? (
          <div className="flex flex-col items-center gap-3 py-20 text-center">
            <div className="grid size-12 place-items-center rounded-full bg-card text-secondary">
              <LifeBuoy className="size-6" />
            </div>
            <p className="max-w-sm text-sm text-muted">
              {status
                ? 'No tickets match this filter.'
                : 'No support tickets yet. Submit one when you need a hand from the FOSS Church team.'}
            </p>
            {canSubmit && !status && (
              <Button variant="secondary" onClick={() => navigate('/app/support/new')}>
                <Plus />
                New ticket
              </Button>
            )}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border text-left text-xs uppercase tracking-wider text-dim">
                  <th className="px-5 py-3 font-semibold">Title</th>
                  <th className="px-5 py-3 font-semibold">Status</th>
                  <th className="px-5 py-3 font-semibold">Priority</th>
                  <th className="px-5 py-3 font-semibold">Submitter</th>
                  <th className="px-5 py-3 font-semibold">Updated</th>
                </tr>
              </thead>
              <tbody>
                {list.map((t: TicketSummary) => (
                  <tr
                    key={t.ticket_id}
                    tabIndex={0}
                    role="link"
                    onClick={() => navigate(`/app/support/${t.ticket_id}`)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault()
                        navigate(`/app/support/${t.ticket_id}`)
                      }
                    }}
                    className="cursor-pointer border-b border-border-soft outline-none transition-colors last:border-0 hover:bg-card-hover focus-visible:bg-card-hover"
                  >
                    <td className="px-5 py-3 font-medium">{t.title}</td>
                    <td className="px-5 py-3">
                      <Badge variant={statusVariant(t.status)}>{humanize(t.status)}</Badge>
                    </td>
                    <td className="px-5 py-3">
                      <Badge variant={priorityVariant(t.priority)}>{humanize(t.priority)}</Badge>
                    </td>
                    <td className="px-5 py-3 text-muted">{t.submitter_email}</td>
                    <td className="px-5 py-3 text-muted">
                      {t.updated_at ? new Date(t.updated_at).toLocaleString() : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  )
}
